import { useEffect } from "react";
import type { SignUpResponse } from "@/lib/api.types";

/**
 * Support for the dealer-site embed.
 *
 * Dealers drop `embed.js` (served by the Rails app at `dealers.approveowl.com`)
 * onto their own site. It iframes {@link EMBED_SIGN_UP_PATH} on their consumer
 * portal domain and talks to the framed page over `postMessage`:
 *
 * - `{ width, height }` — the host script assigns `height` to the iframe. An
 *   iframe never sizes itself to its document, so this has to be re-sent every
 *   time the content grows or shrinks (a validation error appearing, the
 *   thank-you screen replacing the form).
 * - `{ applicantId, sessionUrl, verificationUrl }` — sent once, on a successful
 *   sign-up. The host script hands `applicantId` to the dealer's `onSubmission`
 *   analytics callback and then sends the *top-level* window to
 *   `verificationUrl`, which lands the applicant on the portal's phone-verify
 *   step with the code already sent.
 *
 * The host script authenticates messages by comparing `event.source` against its
 * iframe's `contentWindow`, and we have no way to know the dealer's origin, so we
 * post to `"*"`. Nothing secret goes over this channel — the applicant id is only
 * useful alongside a verified phone number.
 */

export const EMBED_SIGN_UP_PATH = "/sign_up";

export function isEmbedRoute(
  pathname: string = typeof window === "undefined"
    ? ""
    : window.location.pathname,
): boolean {
  return pathname.replace(/\/+$/, "") === EMBED_SIGN_UP_PATH;
}

/**
 * Marks the document as embedded so `styles.css` can strip the standalone-app
 * chrome (viewport-height floor, opaque background, OS dark mode).
 *
 * Called once from `main.tsx` before first paint — an iframe only ever loads
 * straight into the embed route, and doing it in an effect would flash the
 * standalone layout inside the dealer's page — and again from the root route on
 * navigation, which is what keeps it correct under the memory history used in
 * tests.
 */
export function applyEmbedDocumentFlags(pathname?: string) {
  if (typeof document === "undefined") return;
  document.documentElement.toggleAttribute(
    "data-embed",
    isEmbedRoute(pathname),
  );
}

/** The host page, or null when we're not framed (someone opened /sign_up directly). */
function hostWindow(): Window | null {
  if (typeof window === "undefined") return null;
  const parent = window.parent;
  return parent && parent !== window ? parent : null;
}

export function isEmbedded(): boolean {
  return hostWindow() !== null;
}

/**
 * Height of the whole document. Measured off the root element's box rather than
 * `scrollHeight`, because a scroll height is floored at the viewport height —
 * which in an iframe is the height the host just set, so the frame could grow
 * but never shrink back down for the much shorter thank-you screen.
 */
function measureEmbedHeight(): number {
  return Math.ceil(document.documentElement.getBoundingClientRect().height);
}

/**
 * Reports the content size to the host page for as long as the embed is mounted.
 *
 * Both observers earn their place: the mutation observer catches React swapping
 * the DOM (errors, the thank-you screen), the resize observer catches reflow that
 * changes no nodes at all (a web font landing, a line re-wrapping after the host
 * page's own layout settles).
 */
export function useEmbedAutoHeight() {
  useEffect(() => {
    const host = hostWindow();
    if (!host) return;

    let last = { width: -1, height: -1 };

    function report() {
      const next = { width: window.innerWidth, height: measureEmbedHeight() };
      if (next.width === last.width && next.height === last.height) return;
      last = next;
      host?.postMessage(next, "*");
    }

    report();

    const mutations = new MutationObserver(report);
    mutations.observe(document.body, {
      attributes: true,
      characterData: true,
      childList: true,
      subtree: true,
    });

    const resizes = new ResizeObserver(report);
    resizes.observe(document.documentElement);

    window.addEventListener("load", report);
    window.addEventListener("resize", report);

    return () => {
      mutations.disconnect();
      resizes.disconnect();
      window.removeEventListener("load", report);
      window.removeEventListener("resize", report);
    };
  }, []);
}

/**
 * Announces a completed sign-up. Keys are camelCase because that is the shape
 * `embed.js` already reads on dealer sites we can't redeploy.
 */
export function postEmbedSubmission(response: SignUpResponse) {
  hostWindow()?.postMessage(
    {
      // The host script assigns `event.data.height` to the iframe on *every*
      // message it accepts, so a payload without one sets the height to NaN and
      // collapses the frame to the 150px default. It normally sends the top-level
      // window to verificationUrl in the same breath and nobody sees it — but when
      // that navigation is blocked, a collapsed frame is exactly the situation the
      // "Continue Your Application" fallback exists for, and it would be hidden.
      // Send the size we have; useEmbedAutoHeight corrects it a tick later, once
      // the thank-you screen has replaced the form.
      width: window.innerWidth,
      height: measureEmbedHeight(),
      applicantId: response.applicant_id,
      sessionUrl: response.session_url,
      verificationUrl: response.verification_url,
    },
    "*",
  );
}
